import { Component } from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import LazyMarkdownRenderer from "./LazyMarkdownRenderer.tsx";
import { ImgCopy } from "./Images/ImgCopy.tsx";
import { ImgRetry } from "./Images/ImgRetry.tsx";
import { ImgEye } from "./Images/ImgEye.tsx";
import { ImgTrash } from "./Images/ImgTrash.tsx";
import { ImgForward } from "./Images/ImgForward.tsx";
import { ImgClockOutline } from "./Images/ImgClockOutline.tsx";
import { shouldShowModelLoadingProgress } from "./ChatMessageModelLoadingProgress.tsx";
import { formatTokenCount } from "../utils/format.ts";
import type { Message } from "../models.ts";

export interface ContextDividerCardProps {
  message: Message;
  generatingMessageId?: string | null;
  isGenerating?: boolean;
  hasModel?: boolean;
  onRegenerate?: (message: Message) => void;
  onRemove?: (message: Message) => void;
  onCopy?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onViewSource?: () => void;
}

@observer
export class ContextDividerCard extends Component<ContextDividerCardProps> {
  @observable private _isOpen: boolean = false;

  constructor(props: ContextDividerCardProps) {
    super(props);
    makeObservable(this);
  }

  @action.bound
  private _toggle() {
    this._isOpen = !this._isOpen;
  }

  private _fmt = (n: number) => formatTokenCount(n);

  render() {
    const { message, generatingMessageId, isGenerating, hasModel, onRegenerate, onRemove, onCopy, onViewSource } = this.props;
    const isLoading = message.id === 'compact-placeholder' || message.id === generatingMessageId;
    const compactedCount = message.compactedMessageIds?.length ?? 0;
    const tokensIn = message.tokensIn;
    const tokensOut = message.tokensOut;
    const showModelLoadingProgress = isLoading && shouldShowModelLoadingProgress(message);

    return (
      <div className="context-divider">
        <span className="context-divider-line" />
        <div className="context-divider-card">
          <div className="context-divider-header">
            <ImgClockOutline />
            <span className="context-divider-title">
              {isLoading ? 'Summarizing…' : 'Context Compacted'}
            </span>
            {isLoading && !showModelLoadingProgress && (
              <span className="context-spinner" />
            )}
            {!isLoading && compactedCount > 0 && (
              <span className="context-divider-count">{compactedCount} message{compactedCount !== 1 ? 's' : ''}</span>
            )}
            {!isLoading && tokensOut != null && (
              <span className="context-divider-tokens" title="tokens in summarized messages → tokens in resulting summary">
                {tokensIn != null ? `${this._fmt(tokensIn)} → ` : ''}{this._fmt(tokensOut)}
              </span>
            )}
            {isLoading && !!message.content && !showModelLoadingProgress && (
              <span className="context-divider-tokens">~{this._fmt(Math.round(message.content.length / 4))}</span>
            )}
          </div>
          {isLoading && !!message.content && (
            <div className="context-summary-body context-summary-streaming">
              <LazyMarkdownRenderer content={message.content} isGenerating={true} enableFormatting={true} />
            </div>
          )}
          {!isLoading && !!message.content && (
            <>
              <button
                className="context-summary-toggle"
                onClick={this._toggle}
              >
                {this._isOpen ? '▲' : '▼'} View summary
              </button>
              {this._isOpen && (
                <>
                  {tokensIn != null && tokensOut != null && tokensOut > 0 && (
                    <div className="context-compress-stats">
                      <div className="context-compress-bar-wrap">
                        <div className="context-compress-bar-after" style={{ width: `${Math.min((tokensOut / tokensIn) * 100, 100)}%` }} />
                      </div>
                      <div className="context-compress-row">
                        <span className="context-compress-before">summarized: {this._fmt(tokensIn)} tokens</span>
                        <span className="context-compress-arrow"><ImgForward size={12} /></span>
                        <span className="context-compress-after">summary: {this._fmt(tokensOut)} tokens</span>
                        <span className="context-compress-ratio">{(tokensIn / tokensOut).toFixed(1)}× reduction</span>
                      </div>
                    </div>
                  )}
                  <div className="context-summary-body">
                    <LazyMarkdownRenderer content={message.content} isGenerating={false} enableFormatting={true} />
                  </div>
                  {!isGenerating && (
                    <div className="context-card-actions">
                      {message.content && onCopy && (
                        <button className="action-btn action-btn-copy" onClick={(e) => onCopy(e)}>
                          <ImgCopy /> <span>Copy</span>
                        </button>
                      )}
                      {hasModel && onRegenerate && (
                        <button className="action-btn" onClick={() => onRegenerate!(message)}>
                          <ImgRetry /> Regenerate
                        </button>
                      )}
                      {!!message.compactedMessageIds?.length && onViewSource && (
                        <button className="action-btn" onClick={onViewSource}>
                          <ImgEye size={16} /> View source
                        </button>
                      )}
                      {onRemove && (
                        <button className="action-btn" onClick={() => onRemove(message)}>
                          <ImgTrash /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <span className="context-divider-line" />
      </div>
    );
  }
}
